import { api } from "./api.js";

/**
 * Assinatura de Web Push.
 *
 * No iPhone isto SÓ funciona com o app instalado na tela inicial (iOS 16.4+).
 * Safari em aba não recebe push, e é a dúvida nº 1 de quem tenta usar um PWA —
 * por isso detectamos e explicamos, em vez de deixar o botão falhar em silêncio.
 */

export function ehIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function estaInstalado(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function podeReceberPush(): { pode: boolean; motivo: string | null } {
  if (!("serviceWorker" in navigator)) {
    return { pode: false, motivo: "Este navegador não suporta service worker." };
  }
  if (!("PushManager" in window)) {
    return { pode: false, motivo: "Este navegador não suporta Web Push." };
  }
  if (ehIOS() && !estaInstalado()) {
    return {
      pode: false,
      motivo:
        "No iPhone, o Mostarda só consegue te notificar se estiver instalado na tela inicial. Toque em Compartilhar → Adicionar à Tela de Início e abra por lá.",
    };
  }
  return { pode: true, motivo: null };
}

export async function registrarServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (err) {
    console.error("service worker não registrou:", err);
    return null;
  }
}

export async function assinarPush(): Promise<{ ok: boolean; motivo?: string }> {
  const check = podeReceberPush();
  if (!check.pode) return { ok: false, motivo: check.motivo ?? undefined };

  const permissao = await Notification.requestPermission();
  if (permissao !== "granted") {
    return { ok: false, motivo: "Sem permissão de notificação, o Mostarda não consegue te cobrar." };
  }

  const registro = await navigator.serviceWorker.ready;
  const { publicKey } = await api.get<{ publicKey: string }>("/push/key");

  const existente = await registro.pushManager.getSubscription();
  const inscricao =
    existente ??
    (await registro.pushManager.subscribe({
      // Obrigatório: sem isto o navegador recusa. Também é o que garante que
      // nenhum push anônimo chegue ao aparelho.
      userVisibleOnly: true,
      applicationServerKey: base64UrlParaBytes(publicKey),
    }));

  const json = inscricao.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } };
  if (!json.endpoint || !json.keys) return { ok: false, motivo: "Inscrição incompleta." };

  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  return { ok: true };
}

/**
 * O TypeScript 5.9 parametriza arrays tipados pelo buffer que os respalda, e
 * `applicationServerKey` exige `Uint8Array<ArrayBuffer>`. Alocar o ArrayBuffer
 * explicitamente estreita o tipo sem recorrer a `as`.
 */
function base64UrlParaBytes(texto: string): Uint8Array<ArrayBuffer> {
  const preenchido = texto.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(texto.length / 4) * 4, "=");
  const binario = atob(preenchido);
  const bytes = new Uint8Array(new ArrayBuffer(binario.length));
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
}
